import MaskedInput from 'react-text-mask';

import {
  Base,
  BaseEnvironment,
  Container,
  Label,
  LabelEnvironment,
  Error,
  IconWrapper,
} from './styles';
import { KeyboardEvent, ReactNode } from 'react';

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  name?: string;
  disabled?: boolean;
  error?: string | null;
  mask?: any;
  suffix?: string
  noSuffixBorder?: boolean;
  width?: string
  onEnterKey?: () => void;
  handleKeyPress?: (event) => void;
  onWheel?: (event: React.WheelEvent<HTMLInputElement>) => void;
  icon?: {
    iconElement: ReactNode;
    placement: 'end'
  };
  guide?: boolean;
  formLabel?: string;
}

export const Input = ({
  label,
  placeholder,
  value,
  name,
  disabled,
  error,
  mask,
  suffix,
  noSuffixBorder,
  width,
  defaultValue,
  onEnterKey,
  handleKeyPress,
  onWheel,
  ...props
}: CustomInputProps): JSX.Element => {
  const onKeyPress = (e) => {
    if (handleKeyPress) handleKeyPress(e);

    if (e.nativeEvent && e.nativeEvent.keyCode === 13 && onEnterKey) {
      e.preventDefault(); // Ensure it is only this code that runs
      onEnterKey();
    }
  };

  return (
    <>
      {mask ? (
        <InputMasked
          {...props}
          value={value}
          name={name}
          error={error}
          label={label}
          disabled={disabled}
          mask={mask}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          suffix={suffix}
          width={width}
        />
      ) : (
        <InputDefault
          {...props}
          value={value}
          name={name}
          error={error}
          label={label}
          disabled={disabled}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          suffix={suffix}
          noSuffixBorder={noSuffixBorder}
          width={width}
        />
      )}
    </>
  );
};

function InputDefault(cProps: Readonly<{
  value?: string;
  defaultValue?: string;
  disabled?: boolean
  label?: string
  name?: string
  error?: string | null
  placeholder?: string
  suffix?: string
  noSuffixBorder?: boolean
  width?: string
  icon?: {
    iconElement: ReactNode;
    placement: 'end'
  }
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void
}>) {
  const {
    disabled, label, value, defaultValue, name, error, placeholder, suffix, noSuffixBorder, width, icon, ...props
  } = cProps;
  return (
    <Container style={{ width }}>
      <Label disabled={disabled} htmlFor={name} value={value} error={error} suffixLength={suffix?.length} noSuffixBorder={noSuffixBorder}>
        <span>{label}</span>
        <Base {...props} value={value} defaultValue={defaultValue} name={name} error={error} disabled={disabled} placeholder={placeholder} suffixLength={suffix?.length} />
        {suffix && (
          <span>{suffix}</span>
        )}
      </Label>
      {icon && (
        <IconWrapper placement={icon.placement}>
          {icon.iconElement}
        </IconWrapper>
      )}
      {error && <Error>{error}</Error>}
    </Container>
  );
}
export const InputEnvironment = ({
  label,
  placeholder,
  value,
  name,
  disabled,
  error,
  mask,
  suffix,
  noSuffixBorder,
  width,
  defaultValue,
  onEnterKey,
  handleKeyPress,
  onWheel,
  ...props
}: CustomInputProps): JSX.Element => {
  const onKeyPress = (e) => {
    if (handleKeyPress) handleKeyPress(e);

    if (e.nativeEvent && e.nativeEvent.keyCode === 13 && onEnterKey) {
      e.preventDefault();
      onEnterKey();
    }
  };

  return (
    <Container style={{ width }}>
      <LabelEnvironment disabled={disabled} htmlFor={name} value={value} error={error} suffixLength={suffix?.length} noSuffixBorder={noSuffixBorder}>
        <span>{label}</span>
        <BaseEnvironment {...props} value={value} defaultValue={defaultValue} name={name} error={error} disabled={disabled} placeholder={placeholder} suffixLength={suffix?.length} hasLabel={!!label} onKeyPress={onKeyPress} />
        {suffix && (
          <span>{suffix}</span>
        )}
      </LabelEnvironment>
      {error && <Error>{error}</Error>}
    </Container>
  );
};

function InputMasked(cProps: Readonly<{
  value?: string
  disabled?: boolean
  label?: string
  name?: string
  error?: string | null
  mask?: any
  placeholder?: string
  suffix?: string
  newSuffixModel?: string,
  icon?: {
    iconElement: ReactNode;
    placement: 'end'
  }
  width?: string;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void
}>) {
  const {
    disabled, label, value, name, error, mask, placeholder, suffix, icon, width, ...props
  } = cProps;
  return (
    <Container style={{ width }}>
      <Label disabled={disabled} htmlFor={name} value={value} error={error}>
        <span>{label}</span>
        <MaskedInput
          {...props}
          value={value}
          name={name}
          error={error}
          placeholder={placeholder}
          disabled={disabled}
          mask={mask}
          render={(ref, props2) => <Base ref={ref} innerRef={ref} {...props2} />}
        />
        {icon && (
          <IconWrapper placement={icon.placement}>
            {icon.iconElement}
          </IconWrapper>
        )}
      </Label>
      {error && <Error>{error}</Error>}
    </Container>
  );
}
